/**
 * Plano do E-FISIO — status, próximos passos, checklist manual da virada,
 * informações técnicas para a equipe e prompt pronto para a próxima rodada.
 */
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Circle,
  Copy,
  ExternalLink,
  LineChart,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";

import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import { getBlogMonitoring, setBlogChecklistItem } from "@/lib/blog/admin.functions";

export const Route = createFileRoute("/admin/plano-e-fisio")({
  head: () => ({
    meta: [
      { title: "Admin — Plano do E-FISIO" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/plano-e-fisio">
      <PlanoEfisioPage />
    </AdminGate>
  ),
});

const STATUS = [
  { label: "Artigos migrados", value: "42 de 42" },
  { label: "Temas (categorias)", value: "9" },
  { label: "Imagens migradas", value: "339" },
  { label: "Domínio antigo", value: "Redirecionando" },
];

/** Registro completo da migração, em ordem cronológica. */
const CONCLUIDO = [
  {
    fase: "1. Planejamento (12/08/2026)",
    itens: [
      "Plano de migração do e-fisio.wiki.br (WordPress + Elementor) para fisioemortopedia.com.br/e-fisio, revisado para não perder posição no Google.",
      "Regras combinadas: texto dos artigos sem nenhuma alteração por IA, endereços (slugs) idênticos aos do WordPress e datas originais preservadas.",
    ],
  },
  {
    fase: "2. Backup e importação do conteúdo",
    itens: [
      "Cópia integral do WordPress pela API do próprio WordPress: 42 artigos publicados entre set/2022 e jul/2024, com título e descrição de SEO do Rank Math.",
      "339 imagens baixadas, otimizadas (WebP + JPEG progressivo, 21 MB no total) e servidas pelo próprio projeto.",
      "Artigos e 9 temas gravados no banco em markdown, com datas de publicação e de atualização originais.",
      "Importação reexecutável sem duplicar artigos (atualiza pelo endereço do artigo).",
      "Inventário de todos os endereços antigos (com e sem barra no final) guardado para os redirecionamentos.",
    ],
  },
  {
    fase: "3. Blog novo no ar em /e-fisio",
    itens: [
      "Páginas: home do blog, página de cada tema e página de cada artigo.",
      "Home com destaques, faixa de temas, busca e paginação por endereço.",
      "Artigo com bloco “Em resumo” (tirado do próprio texto, sem IA), índice de seções, barra de progresso de leitura, controle de tamanho do texto, imagens que ampliam ao clicar, botões de compartilhar, caixa do autor, “Leia também” com 3 indicações e artigo anterior / próximo.",
      "Chamada para a pós da FEO ligada ao tema de cada artigo.",
      "Visual com o logo oficial do E-FISIO, banner com busca em todas as páginas, artigo em fundo branco e rodapé próprio com os logos E-FISIO e FEO.",
      "Faixas, banner e pop-up da Black levando para a lista da Black; a inscrição de newsletter foi retirada do blog, mantendo só a chamada da Black.",
    ],
  },
  {
    fase: "4. Google e buscas por IA",
    itens: [
      "Cada página com título, descrição, endereço oficial e imagem de compartilhamento (capa do artigo).",
      "Dados estruturados: Blog na home, coleção e trilha de navegação nos temas, artigo e trilha em cada post e perguntas frequentes só quando o artigo já tem perguntas nos subtítulos.",
      "Sitemap com o blog, os temas e cada artigo, usando a data real de atualização do artigo.",
      "Feed RSS do blog no ar.",
      "Blog liberado no robots.txt e os 42 artigos listados no llms.txt (lido pelas buscas com IA).",
      "Título e descrição do Google ajustados nos 15 artigos com mais aparições e poucos cliques.",
    ],
  },
  {
    fase: "5. Painel do blog no admin",
    itens: [
      "Painel em tema claro com as cores do blog: Dashboard (visitas, artigos mais lidos, saúde de SEO) e Artigos.",
      "Editor visual para professores, sem código: título, resumo, texto com botões de formatação, modelo de referências ABNT, autor, data, capa e temas.",
      "Endereço do artigo e textos para o Google criados automaticamente a partir do título e do resumo quando ficam em branco.",
      "Aviso de endereço repetido; ao trocar o endereço de um artigo publicado, o antigo passa a redirecionar sozinho.",
      "Envio de capas pelo painel, guardadas em local privado e exibidas pelo site.",
      "Configurações só para admins: monitoramento dos redirecionamentos, endereços antigos sem destino, checklist da virada, teste em lote dos 301 e backups.",
    ],
  },
  {
    fase: "6. Backup da primeira versão (14/10/2026)",
    itens: [
      "Conteúdo da primeira versão do blog salvo no painel, com opção de baixar, restaurar e criar novos backups.",
      "Layout da primeira versão guardado no projeto como cópia de segurança.",
    ],
  },
  {
    fase: "7. Virada do domínio (14/10/2026)",
    itens: [
      "e-fisio.wiki.br e www.e-fisio.wiki.br conectados a este projeto, DNS apontado no registro.br e certificado de segurança ativo nos dois.",
      "Todos os endereços antigos levam direto (um único redirecionamento 301) ao artigo ou tema equivalente no blog novo; nada vai para a home quando existe destino certo.",
      "Testados: home, www, artigo, tema, /category/, sitemap_index.xml e /feed/.",
      "Endereços antigos sem destino ficam registrados no painel, nunca viram página vazia.",
      "Search Console: mudança de endereço feita, sitemap novo lido sem erros e indexação pedida para os artigos de maior tráfego.",
    ],
  },
  {
    fase: "8. Revisão geral dos artigos (15/10/2026)",
    itens: [
      "Conferência de que nenhum artigo ficou para trás: 42 de 42 publicados.",
      "40 artigos com formatação, links e referências corrigidos, sem mexer no texto.",
      "80 imagens que no WordPress tinham nomes genéricos repetidos (Figura-1.png etc.) trocadas pelas originais certas, cada artigo com sua própria pasta de imagens.",
    ],
  },
  {
    fase: "9. Ajustes de leitura e autores (16/10/2026)",
    itens: [
      "Imagens dos artigos exibidas no tamanho real, centralizadas, sem esticar; só diminuem em telas menores. Conferido que o WordPress já tinha as mesmas dimensões (sem perda na migração).",
      "47 títulos em negrito que apareciam com asteriscos (**) corrigidos em 15 artigos, sem mudar o texto; ajustado também o itálico do resumo do artigo Whiplash.",
      "Índice do artigo em coluna à esquerda no computador, acompanhando a rolagem; no celular continua no topo.",
      "Caixa “Escrito por” com foto do autor (9 autores com foto do corpo docente) e botões Currículo Lattes e Instagram.",
      "Botão “Editar” em cada artigo na lista do painel.",
      "Revisão no celular de todas as páginas do blog: busca sem zoom no iPhone e nada saindo da largura da tela.",
    ],
  },
];

const COMPARATIVO: { item: string; antes: string; agora: string }[] = [
  { item: "Endereço", antes: "e-fisio.wiki.br", agora: "fisioemortopedia.com.br/e-fisio (o antigo redireciona)" },
  { item: "Plataforma", antes: "WordPress + Elementor em hospedagem própria (Wap Integrada)", agora: "Dentro do site da FEO, sem hospedagem separada" },
  { item: "Conteúdo", antes: "42 artigos e 9 temas", agora: "Os mesmos 42 artigos e 9 temas, texto idêntico e datas originais" },
  { item: "Imagens", antes: "Arquivos originais, vários com nomes repetidos", agora: "339 imagens otimizadas (WebP), separadas por artigo" },
  { item: "SEO", antes: "Plugin Rank Math", agora: "Títulos e descrições do Rank Math preservados + dados estruturados, sitemap, RSS e llms.txt" },
  { item: "Leitura", antes: "Página simples do tema", agora: "Resumo, índice, progresso de leitura, tamanho do texto, imagens ampliáveis, leia também, anterior/próximo" },
  { item: "Publicação", antes: "Painel do WordPress", agora: "Editor visual no admin da FEO, com SEO automático" },
  { item: "Ligação com a FEO", antes: "Site separado", agora: "Chamada da pós por tema e campanhas da FEO (ex.: Black) no blog" },
  { item: "Acompanhamento", antes: "Sem painel próprio", agora: "Dashboard de visitas, saúde de SEO e monitoramento dos redirecionamentos" },
  { item: "Backup", antes: "Depende da hospedagem", agora: "Backups no painel, com baixar e restaurar" },
];

const PENDENCIAS = [
  {
    tarefa: "Renovar o domínio e-fisio.wiki.br por pelo menos 1 ano (para os redirecionamentos continuarem)",
    quando: "Antes de 04/11/2026",
    onde: "registro.br → e-fisio.wiki.br → Renovar",
  },
  {
    tarefa: "Trocar links que ainda apontam para e-fisio.wiki.br (Instagram, YouTube, e-mails, RD Station, anúncios, PDFs)",
    quando: "Quando puder",
    onde: "Cada canal",
  },
  {
    tarefa: "Cancelar a hospedagem do WordPress",
    quando: "A partir de meados de janeiro/2027 (90 dias após a virada)",
    onde: "Hospedagem antiga (Wap Integrada)",
  },
  {
    tarefa: "Enviar os links do Currículo Lattes e do Instagram de cada autor (os botões estão no ar, mas sem link)",
    quando: "Quando puder",
    onde: "Mandar os links no chat do projeto",
  },
  {
    tarefa: "Enviar as fotos de Gabriel Comerio e Patrícia Mentges (hoje aparecem só as iniciais)",
    quando: "Quando puder",
    onde: "Mandar as fotos no chat do projeto",
  },
  {
    tarefa: "Confirmar o nome da autora: “Tamires Melo” (artigos) ou “Tamiris Melo” (corpo docente)",
    quando: "Quando puder",
    onde: "Responder no chat do projeto",
  },
  {
    tarefa: "Enviar as imagens originais em tamanho maior, se os autores tiverem (as do WordPress já eram pequenas)",
    quando: "Opcional",
    onde: "Mandar os arquivos no chat do projeto",
  },
];

const REGRAS = [
  "Nunca reescrever ou gerar conteúdo dos artigos com IA — o texto é o mesmo do WordPress.",
  "Slugs idênticos aos do WordPress; muda só o prefixo (/{MM}/ → /e-fisio/).",
  "Datas originais preservadas em todo lugar, inclusive no lastmod do sitemap.",
  "Nenhuma URL antiga vai para a home quando existe destino equivalente; sem correspondência = log, nunca soft 404.",
  "Conteúdo sempre em markdown no banco; nada de artigo hardcoded no código.",
  "Toda escrita do blog passa por server function com verificação de admin.",
];

const TECNICO: { titulo: string; linhas: string[] }[] = [
  {
    titulo: "Rotas",
    linhas: [
      "Públicas: src/routes/e-fisio.tsx (layout), e-fisio.index.tsx (home), e-fisio.categoria.$slug.tsx, e-fisio.$slug.tsx.",
      "Admin: src/routes/admin.e-fisio.tsx, admin.e-fisio_.configuracoes.tsx (abas em src/components/admin/e-fisio/) e admin.plano-e-fisio.tsx. RSS: e-fisio.rss[.]xml.ts.",
      "Sitemap: src/routes/sitemap[.]xml.ts inclui /e-fisio, categorias e cada post com lastmod do banco.",
    ],
  },
  {
    titulo: "Banco de dados",
    linhas: [
      "blog_posts: slug, titulo, subtitulo, autor, autor_cidade, capa_url, conteudo_md, categoria_slugs[], status, seo_title, seo_description, legacy_paths[], published_at, updated_at.",
      "blog_categories: slug, nome, ordem.",
      "Permissões: público lê apenas status = 'published'; rascunho e escrita só para admin.",
      "site_config.blog_efisio_checklist guarda o checklist da virada; site_config.blog_efisio_legacy_hits guarda a contagem agregada de 301 por dia.",
    ],
  },
  {
    titulo: "Server functions (src/lib/blog/)",
    linhas: [
      "blog.functions.ts — leitura pública (home, categoria, artigo + relacionados).",
      "admin.functions.ts — dashboard, listar/abrir/salvar/duplicar/excluir post, categorias, monitoramento e checklist. Todas com verificação de admin na rota /admin/e-fisio.",
      "resumo.ts — extração determinística do bloco “Em resumo” e das perguntas do FAQ, direto do markdown (sem IA).",
      "pos-por-categoria.ts — mapeia categoria do artigo para a pós recomendada no CTA.",
    ],
  },
  {
    titulo: "Redirecionamento 301",
    linhas: [
      "src/start.ts registra legacyDomainMiddleware como primeiro requestMiddleware; ele age quando o Host é e-fisio.wiki.br ou www.e-fisio.wiki.br.",
      "src/lib/blog/legacy-redirects.ts tem o mapa puro (a partir de legacy-inventory.json) e a normalização de barra final e maiúsculas.",
      "legacy-redirects.server.ts complementa com blog_posts.legacy_paths (cache de 10 min) e registra o evento legacy_redirect_miss quando não há destino.",
      "legacy-hits.server.ts agrega os acertos por dia/host — nunca uma linha por acesso.",
      "Em desenvolvimento não é possível testar: o servidor local recusa Host falso. Testar só em produção.",
    ],
  },
  {
    titulo: "Imagens",
    linhas: [
      "Migradas: public/e-fisio-media (servidas estaticamente pelo projeto).",
      "Capas novas: POST /api/admin-blog-upload (bearer + admin) grava no bucket privado blog-media; leitura pública em /api/public/blog-media/*.",
      "Todo <img> do artigo sai com width/height e lazy loading.",
    ],
  },
  {
    titulo: "Reimportação (se algum dia precisar)",
    linhas: [
      "POST /api/public/hooks/import-efisio com o header x-cron-secret; payload em public/e-fisio-import.json.",
      "É idempotente: faz upsert por slug, sem duplicar artigo.",
    ],
  },
  {
    titulo: "Quando aparecer URL antiga sem destino",
    linhas: [
      "Ela aparece na aba Monitoramento com host, número de acessos, data do último acesso e referer.",
      "Achar o artigo equivalente, acrescentar o caminho antigo em blog_posts.legacy_paths e reconferir o 301.",
      "Sem equivalente, o destino continua /e-fisio — nunca criar página vazia só para responder.",
    ],
  },
];


function Bloco({
  icone,
  titulo,
  children,
}: {
  icone: React.ReactNode;
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-admin-accent">
        {icone}
        {titulo}
      </h2>
      {children}
    </section>
  );
}

function PlanoEfisioPage() {


  return (
    <div className="min-h-screen px-4 py-8 text-white sm:px-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-admin-accent">
            <ArrowLeft className="h-3.5 w-3.5" /> Painel
          </Link>
          <h1 className="mt-2 flex items-center gap-2 text-2xl font-semibold">
            <BookOpen className="h-6 w-6 text-admin-accent" /> Plano do E-FISIO
          </h1>
          <p className="text-sm text-white/50">
            Registro completo da migração do E-FISIO do WordPress para o site da FEO. As pendências estão no final.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link
              to="/admin/e-fisio"
              className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 text-xs text-white/70 hover:text-admin-accent"
            >
              <LineChart className="h-3.5 w-3.5" /> Painel do blog
            </Link>
            <a
              href="/e-fisio"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 text-xs text-white/70 hover:text-admin-accent"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Ver blog
            </a>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          {STATUS.map((s) => (
            <div key={s.label} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-[11px] uppercase tracking-wider text-white/50">{s.label}</p>
              <p className="mt-1 text-2xl font-semibold text-admin-accent">{s.value}</p>
            </div>
          ))}
        </div>

        <Bloco icone={<CheckCircle2 className="h-4 w-4" />} titulo="O que foi feito">
          <div className="space-y-4">
            {CONCLUIDO.map((grupo) => (
              <div key={grupo.fase}>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{grupo.fase}</p>
                <ul className="mt-2 space-y-1.5">
                  {grupo.itens.map((item) => (
                    <li key={item} className="flex gap-2 text-sm text-foreground">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Bloco>

        <Bloco icone={<BookOpen className="h-4 w-4" />} titulo="Antes e depois: WordPress x blog novo">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-white/50">
                  <th className="py-2 pr-3">Item</th>
                  <th className="py-2 pr-3">WordPress (antigo)</th>
                  <th className="py-2">E-FISIO novo</th>
                </tr>
              </thead>
              <tbody>
                {COMPARATIVO.map((c) => (
                  <tr key={c.item} className="border-b border-white/5 align-top">
                    <td className="py-2 pr-3 font-medium text-white/85">{c.item}</td>
                    <td className="py-2 pr-3 text-white/55">{c.antes}</td>
                    <td className="py-2 text-white/85">{c.agora}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Bloco>

        <Bloco icone={<Wrench className="h-4 w-4" />} titulo="Informações técnicas para a equipe">
          <div className="space-y-4">
            {TECNICO.map((grupo) => (
              <div key={grupo.titulo} className="rounded-xl border border-white/10 bg-black/30 p-4">
                <p className="text-sm font-semibold text-white/85">{grupo.titulo}</p>
                <ul className="mt-2 space-y-1.5">
                  {grupo.linhas.map((l) => (
                    <li key={l} className="flex gap-2 text-xs text-white/55">
                      <Circle className="mt-1.5 h-1.5 w-1.5 shrink-0 fill-white/30 text-white/30" />
                      {l}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Bloco>

        <Bloco icone={<ShieldCheck className="h-4 w-4" />} titulo="Regras que não mudam">
          <ul className="space-y-2">
            {REGRAS.map((item) => (
              <li key={item} className="flex gap-2 text-sm text-white/60">
                <Circle className="mt-1.5 h-2 w-2 shrink-0 fill-white/30 text-white/30" />
                {item}
              </li>
            ))}
          </ul>
        </Bloco>
        <Bloco icone={<Circle className="h-4 w-4" />} titulo="Pendências (com você)">
          <ol className="space-y-3">
            {PENDENCIAS.map((p, i) => (
              <li key={p.tarefa} className="flex gap-3 rounded-xl border border-border bg-card p-4">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                  {i + 1}
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground">{p.tarefa}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {p.quando} · {p.onde}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </Bloco>

      </div>
    </div>
  );
}
